import {useCallback, useMemo, useState} from 'react';
import Valid from 'card-validator';
import {formatWithMask, useMaskedInputProps} from 'react-native-mask-input';
import {buildCardSummaryLabelFromNumber, buildMaskFromCard, expiryMask} from '../utils/cardEntry';

export type CardEntryValidationError = | 'CARD_NUMBER_REQUIRED' | 'CARD_NUMBER_INVALID' | 'EXPIRY_REQUIRED'
  | 'EXPIRY_INVALID' | 'CVC_REQUIRED' | 'CVC_INVALID';

type Params = { onChange?: () => void };
type ValidationOptions = { requireExpiry?: boolean };

export const useCardEntryForm = ({onChange}: Params = {}) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvc, setCvc] = useState('');
  const numberValidation = Valid.number(cardNumber);
  const expiryValidation = Valid.expirationDate(expiryDate);
  const {cardNumberMask, codeSizeMask} = buildMaskFromCard(numberValidation.card);
  const values = useMemo(() => ({
    number: formatWithMask({text: cardNumber, mask: cardNumberMask}).unmasked,
    expiry: formatWithMask({text: expiryDate, mask: expiryMask}).unmasked,
    cvc: formatWithMask({text: cvc, mask: codeSizeMask}).unmasked
  }), [cardNumber, cardNumberMask, codeSizeMask, cvc, expiryDate]);

  const cardNumberInputProps = useMaskedInputProps({
    value: cardNumber, mask: cardNumberMask,
    onChangeText: value => {
      setCardNumber(value);
      onChange?.();
    }
  });
  const expiryInputProps = useMaskedInputProps({
    value: expiryDate, mask: expiryMask,
    onChangeText: value => {
      setExpiryDate(value);
      onChange?.();
    }
  });
  const cvcInputProps = useMaskedInputProps({
    value: cvc, mask: codeSizeMask,
    onChangeText: value => {
      setCvc(value);
      onChange?.();
    }
  });

  const reset = useCallback(() => {
    setCardNumber('');
    setExpiryDate('');
    setCvc('');
  }, []);

  const clearCvc = useCallback(() => setCvc(''), []);
  const clearSensitiveFields = useCallback(() => {
    setCardNumber('');
    setCvc('');
  }, []);

  const validate = useCallback((options: ValidationOptions = {}) => {
    if (!values.number) return 'CARD_NUMBER_REQUIRED';
    if (!numberValidation.isValid) return 'CARD_NUMBER_INVALID';
    if (options.requireExpiry !== false) {
      if (!values.expiry) return 'EXPIRY_REQUIRED';
      if (!expiryValidation.isValid) return 'EXPIRY_INVALID';
    }
    if (!values.cvc) return 'CVC_REQUIRED';
    if (values.cvc.length < codeSizeMask.length) return 'CVC_INVALID';
    return undefined;
  }, [codeSizeMask.length, expiryValidation.isValid, numberValidation.isValid, values]);

  return {
    cardBrand: numberValidation.card?.niceType, values, cardNumberInputProps, expiryInputProps, cvcInputProps,
    cardSummaryLabel: buildCardSummaryLabelFromNumber(numberValidation.card?.niceType, values.number), reset, clearCvc,
    clearSensitiveFields, validate
  };
};
